import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useObservable } from 'react-use-observable';

import { FIDCAlert } from 'v2/components/Pages/Dashboard/FIDCAlert';
import RegistryBlockModal from 'v2/components/RegistryBlockModal';
import GlobalWarning from 'v2/components/Shared/GlobalWarning';
import useGetPermissions from 'v2/hooks/useGetPermissions';
import { useRegistryCheck } from 'v2/hooks/useRegistryCheck';
import { useRegistrySyncUser } from 'v2/hooks/useRegistrySyncUser';
import eduzzAccountsService from 'v2/services/eduzzAccount';

import Layout, { Content } from '@eduzz/ui-layout';

import RegistryBlockAlert from '@myeduzz-vertical/root/RegistryBlockAlert';

import AppAlert from './appAlert';
import { NavBar } from './NavBar';
import Toolbar from './Toolbar';

export const TOOLBAR_HEIGHT = 64;

interface IProps {
  children: ReactNode;
}

const LayoutWrapper = ({ children }: IProps) => {
  const { pathname } = useLocation();
  const previousBaseRoute = useRef<string>();
  const [showRegistryBlockModal, setShowRegistryBlockModal] = useState(false);

  const [modules] = useObservable(() => eduzzAccountsService.getBetaModules(), []);
  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);

  const permissions = useGetPermissions();
  const { isSyncing: isSyncingRegistryUser } = useRegistrySyncUser();
  const { data: registryCheck, isFetching, error } = useRegistryCheck();

  const isPartner = user?.acceptence_terms;
  const showRegistryBlock = !!user && !isFetching && !error && !!registryCheck?.block && isPartner;

  useEffect(() => {
    const currentBaseRoute = pathname.replace('/producer', '').split('/')[1];

    if (showRegistryBlock && previousBaseRoute.current !== currentBaseRoute) {
      setShowRegistryBlockModal(true);
      previousBaseRoute.current = currentBaseRoute;
    }
  }, [pathname, showRegistryBlock]);

  const handleCancelRegistryBlockModal = () => {
    setShowRegistryBlockModal(false);
  };

  if (!modules || !user || isSyncingRegistryUser) return null;
  return (
    <>
      <div className='[&>div>.eduzz-ui-layout-content]:mx-auto [&>div>.eduzz-ui-layout-content]:max-w-418'>
        <Layout persistMode={true}>
          <Toolbar />
          <NavBar />
          <Content>
            <div className='mx-auto max-w-341.5'>
              <main id='main-content'>
                <RegistryBlockAlert opened={showRegistryBlock} isOwner={!!permissions?.owned} cancelable={false} />
                <RegistryBlockModal
                  opened={showRegistryBlockModal}
                  isOwner={!!permissions?.owned}
                  cancelable={true}
                  onCancel={handleCancelRegistryBlockModal}
                />
                {isPartner && <GlobalWarning />}
                <FIDCAlert />
                {children}
              </main>
            </div>
          </Content>
        </Layout>
      </div>
      <AppAlert />
    </>
  );
};

export default LayoutWrapper;
